import { BotTypeEnum } from './BotTypeEnum';

/**
 * `allowed_size` is always a number of bytes. Every value below one megabyte is a
 * zero, a leftover from the days it meant megabytes, or junk — never a real budget.
 * Treating those as bytes is what made the uploader reject every possible file.
 */
const MIN_CREDIBLE_SIZE_BYTES = 1 * 1024 * 1024;

/** Lowest tiers advertised on the pricing page. */
export const DEFAULT_KB_FILE_SIZE_BYTES = 1 * 1024 * 1024;
export const DEFAULT_VIDEO_SIZE_BYTES = 30 * 1024 * 1024;

/**
 * The server resolves the document budget of every knowledge base bot — clones
 * included — from the bot_KITTY_03 permission. The front reads it from the same
 * place, or it enforces a limit the server never agreed to.
 * See chatbot-server: services/bots/knowledge-base-assistant.ts
 */
export const KNOWLEDGE_BASE_PERMISSION_BOT_CODE = BotTypeEnum.AI_WITH_KNOWLEDGE_BASE;

type BotConfig = { allowed_size?: number; file_limit?: number };
type BotConfigEntry = { botName: string; config?: BotConfig };

const selectPermission = (botConfigs: BotConfigEntry[] | null | undefined, botName: string) => botConfigs?.find(item => item.botName === botName)?.config;

const resolveSizeLimit = (declared: unknown, fallbackBytes: number): number => {
  const value = Number(declared);

  return Number.isFinite(value) && value >= MIN_CREDIBLE_SIZE_BYTES ? value : fallbackBytes;
};

export const selectKnowledgeBasePermission = (botConfigs?: BotConfigEntry[] | null) => selectPermission(botConfigs, KNOWLEDGE_BASE_PERMISSION_BOT_CODE);

export const selectVideoScenarioPermission = (botConfigs?: BotConfigEntry[] | null) => selectPermission(botConfigs, BotTypeEnum.VIDEO_SCENARIO);

export const resolveKbFileSizeLimit = (config?: BotConfig | null): number => resolveSizeLimit(config?.allowed_size, DEFAULT_KB_FILE_SIZE_BYTES);

export const resolveVideoFileSizeLimit = (config?: BotConfig | null): number => resolveSizeLimit(config?.allowed_size, DEFAULT_VIDEO_SIZE_BYTES);

export const toMegabytes = (bytes: number): number => Math.round(bytes / (1024 * 1024));
